import classNames from 'classnames';
import { html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { AtlFormInputProps } from './form.props';
import { InputType, InputValidation } from './form.types';
import { Sizes, enumAttributeConverter, SizesValuesMap } from '../enum-types';
import { ifDefined } from 'lit/directives/if-defined.js';
import { AtlFormInputBase } from './form-input-group.component';
import { AtlFormChangeEvent } from './form.events';

import * as TagNames from './form.tagnames';

@customElement(TagNames.FormInput)
export class AtlFormInput
   extends AtlFormInputBase
   implements AtlFormInputProps
{
   declare inputId: string;
   declare inputType?: InputType;
   declare label?: string;
   declare inputClassName?: string;
   declare value?: string;
   declare size?: Sizes;
   declare disabled?: boolean;
   declare placeholder?: string;
   declare caption?: string;
   declare validation?: InputValidation;
   declare validationMessage?: string;
   declare showClearIcon?: boolean;
   declare triggerOnKeyUp?: boolean;

   static properties = {
      ...AtlFormInputBase.properties,
      inputId: { type: String },
      label: { type: String },
      inputType: {
         type: String,
         convertor: enumAttributeConverter(InputType, InputType.Text),
      },
      inputClassName: { type: String },
      value: { type: String },
      size: {
         type: String,
         convertor: enumAttributeConverter(Sizes, Sizes.Medium),
      },
      disabled: { type: Boolean },
      placeholder: { type: String },
      inputGroup: {
         attribute: 'input-group',
         type: Object,
      },
      caption: { type: String },
      validation: {
         type: String,
         convertor: enumAttributeConverter(InputValidation),
      },
      validationMessage: { type: String },
      showClearIcon: { type: Boolean },
      triggerOnKeyUp: { type: Boolean },
   };

   private _renderCaption = () => {
      if (!this.caption) return nothing;

      return html`<div class="form-text">${this.caption}</div> `;
   };

   private _renderValidationMessage = () => {
      if (!this.validation || !this.validationMessage) return nothing;

      return html`<div
         class="${classNames(
            'validation-feedback',
            this.validation === InputValidation.Valid && 'valid-feedback',
            this.validation === InputValidation.Invalid && 'invalid-feedback'
         )}"
      >
         ${this.validationMessage}
      </div>`;
   };

   private renderClear = () => {
      if (this.showClearIcon && this.value && this.value?.length > 0) {
         return html`
            <i
               id=${`${this.inputId}-clr`}
               class="fa-solid fa-xmark atl-form-input-clear"
               part="icon"
               @click=${this._clearInput}
            ></i>
         `;
      }
      return nothing;
   };

   protected override _renderInput(): unknown {
      return html` 
            <input
               id=${this.inputId}
               class=${classNames(
                  'form-control',
                  this.inputClassName,
                  SizesValuesMap[this.size ?? Sizes.Medium],
                  this.validation === InputValidation.Valid && 'is-valid',
                  this.validation === InputValidation.Invalid && 'is-invalid',
                  'atl-form-input'
               )}
               title=${this.title}
               type="${ifDefined(this.inputType)}"
               .value="${this.value ?? ''}"
               ?disabled="${this.disabled}"
               @keyup=${(e: Event) => this.triggerOnKeyUp && this._handleChange(e)}
               @change=${(e: Event) => !this.triggerOnKeyUp && this._handleChange(e)}
               placeholder=${ifDefined(this.placeholder)}
            >
            </input>
            ${this._renderCaption()}
            ${this._renderValidationMessage()}
         </div>
         ${this.renderClear()}
      `;
   }

   private _clearInput = () => {
      this.value = undefined;
      this.dispatchEvent(new AtlFormChangeEvent(''));
   };

   private _handleChange = (event: Event) => {
      const newValue =
         ((event.target as HTMLInputElement)?.value as string)?.trim() ?? '';
      this.value = newValue;
      this.dispatchEvent(new AtlFormChangeEvent(newValue));
   };
}
